import React, { useEffect, useState } from "react";
import { Row, Col, Layout, Pagination } from "antd";
import { ListingService } from "services/SkyboxServices";
import CommonReportTable from "utils/table/CommonReportTable";
import { listingHeaders } from "./listingUtils";
import { ICustomError } from "models/interfaces/ICustomError";
import CustomError from "controls/CustomError/CustomError";
import { LoggerService } from "sharedServices/api/logger.service";
import { ListingReportData, IFilterOptions } from "models/interfaces";
import AppliedFilters from "utils/commonFilter/AppliedFilters";
import { AppSetting } from "utils/appSettings";
import { usePageTitle } from "hooks";
import LoadingSpinner from "utils/sharedComponents/LoadingSpinner";
import { formatThousandSeparator } from "utils/commonFunction";

const { Content } = Layout;
const DEFAULT_PAGE_INDEX = AppSetting.DEFAULT_PAGE_INDEX;
const PAGE_SIZE = AppSetting.PAGE_SIZE;

const ListingTable: React.FC<{
  filterObject: IFilterOptions | null;
  appliedFilterValues: any | null;
  searchQuery: string | null;
  outerLoader: boolean;
  setOuterLoader: Function;
}> = ({
  filterObject,
  appliedFilterValues,
  searchQuery,
  outerLoader,
  setOuterLoader
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [defaultQueryParams] = useState<any>({
    page_index: DEFAULT_PAGE_INDEX,
    page_size: PAGE_SIZE,
    sort_column: "listing_date",
    sort_order: "desc",
    keywords: "",
    include_only_listings: true,
  });
  const [queryParams, setQueryParams] = useState<any>(null);
  const [listingData, setListingData] = useState<ListingReportData | null>(null);
  const [reportData, setReportData] = useState<any | null>();
  const [dataFetchError, setDataFetchError] = useState<ICustomError | null>(
    null
  );
  usePageTitle("Listing Report");

  useEffect(() => {
    if (filterObject !== null && defaultQueryParams) {
      setQueryParams({
        ...defaultQueryParams,
        ...queryParams,
        ...filterObject,
        page_index: DEFAULT_PAGE_INDEX,
      });
    }
  }, [defaultQueryParams, filterObject]);

  useEffect(() => {
    if (queryParams != null) {
      const getListingReportData = async (query: any) => {
        setOuterLoader(false);
        setShowLoader(true);
        try {
          const response = await new ListingService().getListingData({
            ...query,
          });
          const responseData = response.data.data;
          setReportData(responseData.listingReports);

          setShowLoader(false);
          setListingData(responseData);
          setDataFetchError(null);
        } catch (error: any) {
          setShowLoader(false);
          await new LoggerService().log({
            payload: error,
            function_name: "getListingReportData",
          });
          setDataFetchError({ ...error, refreshAction: getListingReportData });
        }
      };
      getListingReportData(queryParams);
    }
  }, [queryParams]);

  useEffect(() => {
    if (searchQuery !== null) {
      setQueryParams({
        ...queryParams,
        page_index: DEFAULT_PAGE_INDEX,
        keywords: searchQuery,
      });
    }
  }, [searchQuery]);

  const getViewTable = () => {
    if (!showLoader && listingData && listingData?.total) {
      return (
        <ul className="innerHeaderRight">
          <li>
            <h4>
              {listingData?.total === 1
                ? listingData?.total + " Record Found"
                : formatThousandSeparator(listingData?.total) + " Records Found"}
            </h4>
          </li>
          <li>
            <Pagination
              className="pagination"
              hideOnSinglePage={true}
              current={queryParams?.page_index}
              total={listingData?.total}
              pageSize={queryParams?.page_size}
              onChange={(pageNumber) =>
                setQueryParams({
                  ...queryParams,
                  page_index: pageNumber,
                })
              }
              showSizeChanger={false}
            />
          </li>
        </ul>
      );
    }
    return (
      <div style={{ height: 21 }}></div>
    );
  }

  return (
    <LoadingSpinner isLoading={outerLoader}>
      <React.Fragment>
        {dataFetchError ? (
          <CustomError {...dataFetchError} />
        ) : (
          queryParams && (
            <Content className="mainContainer">
              <div className="innerHeader">
                <Row className="flx justifyContentBetween alignItemsCenter">
                  <Col xs={24} sm={24} md={14} className="gpAppliedFilter">
                    <AppliedFilters appliedFilters={appliedFilterValues} />
                  </Col>
                  <Col  xs={24} sm={24} md={10}>
                    { getViewTable() }
                  </Col>
                </Row>
              </div>
              <div className="removeTblLRSpc">
                <CommonReportTable
                  onSort={(sortIndex: string, sortOrder: "asc" | "desc") => {
                    setQueryParams({
                      ...queryParams,
                      sort_column: sortIndex,
                      sort_order: sortOrder,
                    });
                  }}
                  sortField={{
                    dataIndex: queryParams?.sort_column,
                    order: queryParams?.sort_order,
                  }}
                  headers={listingHeaders}
                  data={(() => { return listingData ? reportData : null })()}
                    headerData={listingData?.aggregate?.map((data) => {
                        return { ...data, listing_date: "Total" };
                      })
                    }
                  tableHeight={{ maxHeight: "calc(100vh - 218px)" }}
                  loading={showLoader}
                />
              </div>
            </Content>
          )
        )}
      </React.Fragment>
    </LoadingSpinner>
  );
};

export default ListingTable;
